import io from "socket.io-client";
import discovery from "../discovery";

type ChannelEvent = {
  message_type?: string;
  message_content?: string;
  service?: string;
  user_id?: string;
  server_timestamp?: number;
};

type ChannelPayload = {
  company_id?: number | string;
  payload?: string;
};

let socket: any = null;
let subscribedChannelName: string | null = null;
let boundMessageHandler: ((event: ChannelEvent) => void) | null = null;

function safeParseMessageContent(messageContent?: string): ChannelPayload {
  if (!messageContent) {
    return {};
  }

  try {
    return JSON.parse(messageContent) as ChannelPayload;
  } catch (error) {
    console.warn("Failed to parse websocket message_content", error);
    return {};
  }
}

function getActiveCompanyId(): string | null {
  return window.localStorage.getItem("worktime.client.company.id");
}

function shouldProcessEvent(payload: ChannelPayload): boolean {
  const activeCompanyId = getActiveCompanyId();
  if (!payload.company_id || !activeCompanyId) {
    return true;
  }

  return String(payload.company_id) === String(activeCompanyId);
}

function buildPushEnvelope(event: ChannelEvent, payload: ChannelPayload): string {
  const isProfilePictureUpdate = event.message_type === "profile-picture-update";

  return JSON.stringify({
    data: {
      type: isProfilePictureUpdate ? "profile-picture-update" : "sync",
      payload: payload.payload || event.message_type || "",
    },
  });
}

function emitAsNativePush(event: ChannelEvent): void {
  const payload = safeParseMessageContent(event.message_content);
  if (!shouldProcessEvent(payload)) {
    return;
  }

  if (typeof window.gwt_onNativeDeviceEvent !== "function") {
    console.warn("gwt_onNativeDeviceEvent is not available");
    return;
  }

  const pushEnvelope = buildPushEnvelope(event, payload);
  window.gwt_onNativeDeviceEvent(`NE_PUSH:${pushEnvelope}`);
}

function triggerReconnectRefresh(): void {
  if (typeof window.gwt_onNativeDeviceEvent === "function") {
    window.gwt_onNativeDeviceEvent("NE_AWAKE:301");
  }

  if (window.$vueBridge && typeof window.$vueBridge.refreshModule === "function") {
    window.$vueBridge.refreshModule();
  }
}

export function useWebSocket() {
  function connect(username?: string | null): void {
    if (!username) {
      return;
    }

    const channelUrl = discovery.socketio_channel_url;
    const environment = discovery.tweem_environment;
    if (!channelUrl || !environment) {
      console.warn("socketio-channel discovery settings are missing");
      return;
    }

    const channelName = `tweem-${environment}-${username}`;

    if (socket && subscribedChannelName === channelName) {
      return;
    }

    disconnect();

    socket = io(channelUrl, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      query: {
        token: window.kc_token || "",
        targetApp: "worktime-client",
        targetEnv: environment,
      },
    });
    subscribedChannelName = channelName;
    boundMessageHandler = (event: ChannelEvent) => emitAsNativePush(event);

    socket.on("connect", () => {
      if (subscribedChannelName) {
        socket?.emit("join-channel", subscribedChannelName);
      }
    });

    socket.on("reconnect", () => {
      if (subscribedChannelName) {
        socket?.emit("join-channel", subscribedChannelName);
      }
      triggerReconnectRefresh();
    });

    socket.on(channelName, boundMessageHandler);
  }

  function disconnect(): void {
    if (!socket) {
      subscribedChannelName = null;
      boundMessageHandler = null;
      return;
    }

    if (subscribedChannelName) {
      if (boundMessageHandler) {
        socket.off(subscribedChannelName, boundMessageHandler);
      }
      socket.emit("leave-channel", subscribedChannelName);
    }

    socket.disconnect();
    socket = null;
    subscribedChannelName = null;
    boundMessageHandler = null;
  }

  return { connect, disconnect };
}
